import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { APOLLO_ERROR_POLICY, APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { useGetMembersQuery } from '@/publicSearch/graphql/queries.generated';
import {
  FieldOption,
  LogicalOperator,
  OperatorOption,
} from '@/components/publicSearch/constants';
import type {
  ComparisonOperator,
  FilterGroupOfMemberFilteredFilterOptionInput,
  MemberFilterField,
} from '@/graphqlClient/types';

export function useMember(memberId: string, orgId: string) {
  const isInvalidId = !memberId || isNaN(Number(memberId));

  const { data, error, loading, previousData } = useGetMembersQuery({
    variables: {
      requestInput: {
        pagination: {
          currentPage: 1,
          pageSize: 1,
        },
        filterGroups: [
          {
            logicalOperator: LogicalOperator.and,
            filters: [
              {
                field: FieldOption.societyId.value as MemberFilterField,
                operator: OperatorOption.equals.value as ComparisonOperator,
                value: orgId,
              },
              {
                field: FieldOption.memberId.value as MemberFilterField,
                operator: OperatorOption.equals.value as ComparisonOperator,
                value: memberId,
              },
            ],
            groups: [],
          },
        ] as FilterGroupOfMemberFilteredFilterOptionInput[],
        sortOptions: [],
      },
    },
    context: {
      headers: setDefaultHeader(orgId),
    },
    skip: !orgId || isInvalidId,
    errorPolicy: APOLLO_ERROR_POLICY.ALL,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    notifyOnNetworkStatusChange: true,
  });

  const member = data?.members?.items?.[0] ?? null;
  const previousMember = previousData?.members?.items?.[0] ?? null;

  // The query succeeded but matched no member. Distinct from "still loading"
  // and from "transient error" — used by callers to render a Not Found state.
  const notFound = !loading && !!data && !member && !error;

  return {
    data: member ? { member } : null,
    previousData: previousMember ? { member: previousMember } : null,
    error,
    loading,
    notFound,
    done: (data || error) && !loading,
  };
}
